import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { PatientFormData, PatientInsurance, ValidationErrors } from '../types';
import { patientService } from '../services/patientService';
import { catalogService, type CatalogItem, type MunicipalityItem, type DistrictItem, type InsurancePlanItem } from '../../../services/catalogService';
import { isValidDominicanCedula } from '../../../lib/dominicanCedula';

const API_FIELD_TO_FORM: Record<string, string> = {
  identification: 'identification',
  identification_type_id: 'identificationType',
  first_name: 'firstName',
  last_name: 'lastName',
  birth_date: 'birthDate',
  nationality_id: 'nationalityId',
  province_id: 'provinceId',
  municipal_district_id: 'municipalDistrictId',
  email: 'email',
  medical_record_number: 'recordNumber',
  gender: 'gender',
};

const initialFormData: PatientFormData = {
  recordNumber: '',
  isActive: true,
  identificationType: 1,
  identification: '',
  gender: true,
  birthDate: '',
  firstName: '',
  lastName: '',
  nationalityId: 0,
  provinceId: null,
  municipalDistrictId: null,
  address: '',
  email: '',
  phone: '',
  mobile: '',
  whatsappOptIn: false,
  whatsappPhone: '',
  insurances: [],
};

export const usePatientRegister = () => {
  const [formData, setFormData] = useState<PatientFormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState<{
    policyNumber?: string;
    nss?: string;
    cardNumber?: string;
    insurancePlan?: PatientInsurance['insurancePlan'];
  }>({
    policyNumber: '',
    nss: '',
    cardNumber: '',
    insurancePlan: undefined,
  });

  // Catalog data
  const [nationalities, setNationalities] = useState<CatalogItem[]>([]);
  const [provinces, setProvinces] = useState<CatalogItem[]>([]);
  const [municipalities, setMunicipalities] = useState<MunicipalityItem[]>([]);
  const [districts, setDistricts] = useState<DistrictItem[]>([]);
  const [identificationTypes, setIdentificationTypes] = useState<CatalogItem[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<CatalogItem[]>([]);
  const [insurancePlans, setInsurancePlans] = useState<InsurancePlanItem[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [catalogsError, setCatalogsError] = useState<string | null>(null);
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [selectedInsuranceCompanyId, setSelectedInsuranceCompanyId] = useState<number | null>(null);
  const [insurancePlansLoading, setInsurancePlansLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      setCatalogsLoading(true);
      setCatalogsError(null);
      try {
        const [nats, provs, idTypes, companies] = await Promise.all([
          catalogService.getNationalities(),
          catalogService.getProvinces(),
          catalogService.getIdentificationTypes(),
          catalogService.getInsuranceCompanies(),
        ]);
        setNationalities(nats);
        setProvinces(provs);
        setIdentificationTypes(idTypes);
        setInsuranceCompanies(companies);
        const dominican = nats.find((nat) => nat.name === 'Dominicana');
        if (dominican) {
          setFormData((prev) =>
            prev.nationalityId === 0
              ? { ...prev, nationalityId: dominican.id }
              : prev
          );
        }
      } catch (err) {
        console.error('Error loading catalogs:', err);
        setCatalogsError('No se pudieron cargar nacionalidad, provincia ni tipos de identificación.');
      } finally {
        setCatalogsLoading(false);
      }
    };
    loadCatalogs();
  }, []);

  // Cascade: when province changes, fetch municipalities
  const handleProvinceChange = useCallback(async (provinceId: number | null) => {
    setFormData(prev => ({ ...prev, provinceId, municipalDistrictId: null }));
    setMunicipalities([]);
    setDistricts([]);
    setSelectedMunicipalityId(null);
    if (errors['provinceId']) setErrors(prev => ({ ...prev, provinceId: '' }));

    if (!provinceId) return;

    try {
      const munis = await catalogService.getMunicipalities(provinceId);
      setMunicipalities(munis);
    } catch (err) {
      console.error('Error loading municipalities:', err);
    }
  }, [errors]);

  // Cascade: when municipality changes, fetch districts
  const handleMunicipalitySelect = useCallback(async (municipalityId: number | null) => {
    setSelectedMunicipalityId(municipalityId);
    setFormData(prev => ({ ...prev, municipalDistrictId: null }));
    setDistricts([]);

    if (!municipalityId) {
      setDistrictsLoading(false);
      return;
    }

    setDistrictsLoading(true);
    try {
      const dists = await catalogService.getDistricts(municipalityId);
      setDistricts(dists);
    } catch (err) {
      console.error('Error loading districts:', err);
    } finally {
      setDistrictsLoading(false);
    }
  }, []);

  const handleDistrictChange = useCallback((districtId: number | null) => {
    setFormData(prev => ({ ...prev, municipalDistrictId: districtId }));
  }, []);

  const dominicanNationalityId = useCallback(
    (list: CatalogItem[] = nationalities) =>
      list.find((nat) => nat.name === 'Dominicana')?.id ?? 0,
    [nationalities]
  );

  const handleInsuranceCompanySelect = useCallback(async (companyId: number | null) => {
    setSelectedInsuranceCompanyId(companyId);
    setInsuranceForm((prev) => ({ ...prev, insurancePlan: undefined }));
    setInsurancePlans([]);
    if (!companyId) {
      setInsurancePlansLoading(false);
      return;
    }
    setInsurancePlansLoading(true);
    try {
      const plans = await catalogService.getInsurancePlans(companyId);
      setInsurancePlans(plans);
    } catch (err) {
      console.error('Error loading insurance plans:', err);
    } finally {
      setInsurancePlansLoading(false);
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.recordNumber.trim()) newErrors.recordNumber = 'El número de expediente es obligatorio';
    if (!formData.identification.trim()) newErrors.identification = 'La identificación es obligatoria';
    if (!formData.firstName.trim()) newErrors.firstName = 'El nombre es obligatorio';
    if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es obligatorio';
    if (!formData.birthDate) newErrors.birthDate = 'La fecha de nacimiento es obligatoria';
    if (formData.nationalityId === 0) newErrors.nationalityId = 'La nacionalidad es obligatoria';
    if (!formData.provinceId) newErrors.provinceId = 'La provincia es obligatoria';
    if (!formData.identificationType) {
      newErrors.identificationType = 'El tipo de identificación es obligatorio';
    }
    const districtCatalogEmpty =
      selectedMunicipalityId != null && !districtsLoading && districts.length === 0;
    if (!formData.municipalDistrictId && !districtCatalogEmpty) {
      newErrors.municipalDistrictId = 'El distrito municipal es obligatorio';
    }

    const cedulaTypeId = identificationTypes.find((t) => t.name === 'Cédula')?.id;
    const isCedula = cedulaTypeId != null
      ? formData.identificationType === cedulaTypeId
      : formData.identificationType === 1;

    if (isCedula && formData.identification.trim()) {
      const idRegex = /^\d{3}-\d{7}-\d{1}$/;
      if (!idRegex.test(formData.identification)) {
        newErrors.identification = 'Formato de identificación inválido (000-0000000-0)';
      } else if (!isValidDominicanCedula(formData.identification)) {
        newErrors.identification = 'La cédula no es válida';
      }
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Correo electrónico inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, identificationTypes, selectedMunicipalityId, districts.length, districtsLoading]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setFormError(null);
    setSuccessMessage(null);
    try {
      const payload = {
        medical_record_number: formData.recordNumber,
        status: formData.isActive ? '1' : '0',
        identification_type_id: formData.identificationType,
        identification: formData.identification,
        gender: formData.gender,
        birth_date: formData.birthDate,
        first_name: formData.firstName,
        last_name: formData.lastName,
        nationality_id: formData.nationalityId,
        province_id: formData.provinceId,
        municipal_district_id: formData.municipalDistrictId,
        address: formData.address,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
        whatsapp_opt_in: formData.whatsappOptIn,
        whatsapp_phone: formData.whatsappPhone || formData.mobile,
        insurances: formData.insurances,
      };

      await patientService.createOrUpdate(payload);
      setFormData({
        ...initialFormData,
        nationalityId: dominicanNationalityId(),
      });
      setSelectedMunicipalityId(null);
      setMunicipalities([]);
      setDistricts([]);
      setDistrictsLoading(false);
      setSelectedInsuranceCompanyId(null);
      setInsurancePlans([]);
      setInsuranceForm({
        policyNumber: '',
        nss: '',
        cardNumber: '',
        insurancePlan: undefined,
      });
      setErrors({});
      setSuccessMessage('Paciente registrado correctamente');
    } catch (error) {
      console.error('Error registrando paciente:', error);
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const apiErrors = error.response.data?.errors as Record<string, string[] | string> | undefined;
        const fields: ValidationErrors = {};
        if (apiErrors && typeof apiErrors === 'object') {
          for (const [key, msgs] of Object.entries(apiErrors)) {
            const formKey = API_FIELD_TO_FORM[key] ?? key;
            const msg = Array.isArray(msgs) ? msgs[0] : msgs;
            if (msg) fields[formKey] = msg;
          }
        }
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          setFormError('Revise los campos marcados.');
        } else {
          setFormError(error.response.data?.message || 'Error al registrar el paciente');
        }
      } else if (axios.isAxiosError(error)) {
        setFormError(error.response?.data?.message || 'Error al registrar el paciente');
      } else {
        setFormError('Error al registrar el paciente');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, dominicanNationalityId]);

  const handleInputChange = useCallback((field: keyof PatientFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field] || (field === 'identificationType' && errors.identification)) {
      setErrors(prev => {
        const next = { ...prev, [field]: '' };
        if (field === 'identificationType') next.identification = '';
        return next;
      });
    }

    // Cascade triggers
    if (field === 'provinceId') {
      handleProvinceChange(value);
    }
  }, [errors, handleProvinceChange]);

  const addInsurance = useCallback(() => {
    if (!insuranceForm.nss?.trim()) {
      alert('El NSS es obligatorio');
      return;
    }
    if (!insuranceForm.insurancePlan) {
      alert('El plan de seguro es obligatorio');
      return;
    }

    const newInsurance: PatientInsurance = {
      policyNumber: insuranceForm.policyNumber || '',
      nss: insuranceForm.nss,
      cardNumber: insuranceForm.cardNumber || '',
      insurancePlan: insuranceForm.insurancePlan,
    };

    setFormData(prev => ({
      ...prev,
      insurances: [...prev.insurances, newInsurance],
    }));

    setInsuranceForm({
      policyNumber: '',
      nss: '',
      cardNumber: '',
      insurancePlan: undefined,
    });
  }, [insuranceForm]);

  const removeInsurance = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      insurances: prev.insurances.filter((_, i) => i !== index),
    }));
  }, []);

  const clearForm = useCallback(() => {
    setFormData({
      ...initialFormData,
      nationalityId: dominicanNationalityId(),
    });
    setErrors({});
    setFormError(null);
    setSuccessMessage(null);
    setMunicipalities([]);
    setDistricts([]);
    setSelectedMunicipalityId(null);
    setDistrictsLoading(false);
    setSelectedInsuranceCompanyId(null);
    setInsurancePlans([]);
    setInsuranceForm({
      policyNumber: '',
      nss: '',
      cardNumber: '',
      insurancePlan: undefined,
    });
  }, [dominicanNationalityId]);

  return {
    formData,
    errors,
    loading,
    catalogsLoading,
    catalogsError,
    formError,
    successMessage,
    insuranceForm,
    nationalities,
    provinces,
    municipalities,
    districts,
    identificationTypes,
    insuranceCompanies,
    insurancePlans,
    selectedMunicipalityId,
    districtsLoading,
    selectedInsuranceCompanyId,
    insurancePlansLoading,
    handleSubmit,
    handleInputChange,
    handleMunicipalitySelect,
    handleDistrictChange,
    handleInsuranceCompanySelect,
    addInsurance,
    removeInsurance,
    clearForm,
    setInsuranceForm,
  };
};
